/**
 * NavigatorX Enhanced User Detail Modal
 *
 * Comprehensive user detail view with:
 * - Profile information & device info
 * - Revenue summary & financial overview
 * - Subscription status, history & payment transactions
 * - Referral statistics, invite pipeline & conversion tracking
 * - Voice clones management with quality metrics
 * - Hazard reports with stats & cancellation feedback
 * - Favorites list & navigation history
 * - Support tickets & user feedback
 * - Activity timeline: app events, login history, notifications, admin audit logs
 *
 * @author Claude AI for TGE.LA
 * @version 2.0.0 - Full service integration: billing, referrals, analytics, support
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  X,
  User,
  Mail,
  Phone,
  Building2,
  Smartphone,
  Crown,
  Clock,
  Star,
  Gift,
  AlertCircle,
  Navigation,
  Activity,
  Calendar,
  MapPin,
  CheckCircle,
  Shield,
  History,
  Bell,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  Edit,
  Trash2,
  Mic,
  Volume2,
  RotateCcw,
  Power,
  AlertTriangle,
  XCircle,
  DollarSign,
  Users,
  CreditCard,
  MessageSquare,
  HelpCircle,
  LogIn,
  MousePointerClick,
  Send,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import {
  userSubcollectionRef,
  SUBCOLLECTIONS,
  timestampToDate,
  getUserReferralStats,
  getUserReferralInvites
} from '../../../utils/services/navigatorXService';
import {
  getLogsForTarget,
  getLoginAttempts,
  type LoginAttempt
} from '../../../utils/services/navigatorXAuditService';
import {
  getUserVoices,
  getUserCreatedVoices,
  getUserMediaClonedVoices,
  deleteCreatedVoice,
  deleteMediaClonedVoice,
  unpublishCreatedVoice,
  toggleVoiceActive,
  retryVoiceProcessing,
  type VoiceClone,
  type CreatedVoiceProfile,
  type MediaClonedVoiceProfile
} from '../../../utils/services/navigatorXVoiceService';
import {
  getUserTransactions,
  SUBSCRIPTION_PRICE,
  type Transaction
} from '../../../utils/services/navigatorXBillingService';
import {
  getUserEvents,
  type AnalyticsEvent
} from '../../../utils/services/navigatorXAnalyticsService';
import {
  lookupUserForSupport,
  type SupportTicket,
  type UserFeedback
} from '../../../utils/services/navigatorXSupportService';
import { auth } from '../../../utils/firebase/client';
import type {
  NavUser,
  FavoriteWell,
  WellHistoryEntry,
  AdminAuditLog,
  UserHazardReport,
  ReferralStats,
  ReferralInvite,
  AppNotification,
  CancellationFeedbackEntry
} from '../../../types/navigatorx';

const MILLISECONDS_PER_DAY = 86_400_000;

function getVoiceStatusColor(status: string): string {
  switch (status) {
    case 'READY': return 'bg-green-100 text-green-700';
    case 'PROCESSING': return 'bg-yellow-100 text-yellow-700';
    case 'RECORDING': return 'bg-blue-100 text-blue-700';
    case 'GENERATING_PREVIEW': return 'bg-blue-100 text-blue-700';
    case 'PREVIEW_READY': return 'bg-cyan-100 text-cyan-700';
    case 'REGENERATING': return 'bg-indigo-100 text-indigo-700';
    case 'FINALIZING': return 'bg-violet-100 text-violet-700';
    case 'FAILED': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getHazardTypeColor(type: string): string {
  switch (type?.toUpperCase()) {
    case 'ACCIDENT': return 'bg-red-100 text-red-700';
    case 'ROAD_CLOSURE': return 'bg-orange-100 text-orange-700';
    case 'CONSTRUCTION': return 'bg-yellow-100 text-yellow-700';
    case 'POTHOLE': return 'bg-gray-100 text-gray-700';
    case 'DEBRIS': return 'bg-amber-100 text-amber-700';
    case 'FLOODING': return 'bg-blue-100 text-blue-700';
    case 'ICE': return 'bg-cyan-100 text-cyan-700';
    case 'POLICE': return 'bg-indigo-100 text-indigo-700';
    case 'ANIMAL': return 'bg-green-100 text-green-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getTicketStatusColor(status: string): string {
  switch (status) {
    case 'open': return 'bg-red-100 text-red-700';
    case 'in_progress': return 'bg-yellow-100 text-yellow-700';
    case 'waiting_user': return 'bg-blue-100 text-blue-700';
    case 'resolved': return 'bg-green-100 text-green-700';
    case 'closed': return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getTicketPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent': return 'bg-red-100 text-red-700';
    case 'high': return 'bg-orange-100 text-orange-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    case 'low': return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

interface UserDetailModalProps {
  user: NavUser;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRefresh: () => void;
}

export default function UserDetailModal({
  user,
  onClose,
  onEdit,
  onDelete,
  onRefresh
}: UserDetailModalProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteWell[]>([]);
  const [wellHistory, setWellHistory] = useState<WellHistoryEntry[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [auditLogs, setAuditLogs] = useState<AdminAuditLog[]>([]);
  const [voiceClones, setVoiceClones] = useState<VoiceClone[]>([]);
  const [createdVoices, setCreatedVoices] = useState<CreatedVoiceProfile[]>([]);
  const [mediaClonedVoices, setMediaClonedVoices] = useState<MediaClonedVoiceProfile[]>([]);
  const [voiceProcessing, setVoiceProcessing] = useState<Set<string>>(new Set());
  const [hazardReports, setHazardReports] = useState<UserHazardReport[]>([]);
  const [cancellationFeedback, setCancellationFeedback] = useState<CancellationFeedbackEntry[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [referralInvites, setReferralInvites] = useState<ReferralInvite[]>([]);
  const [userEvents, setUserEvents] = useState<AnalyticsEvent[]>([]);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [supportProfile, setSupportProfile] = useState<{ tickets: SupportTicket[]; feedback: UserFeedback[] } | null>(null);
  const [totalRevenue, setTotalRevenue] = useState<number>(0);

  const adminId = auth.currentUser?.uid || 'unknown';
  const combinedVoices = [...voiceClones, ...createdVoices, ...mediaClonedVoices];
  const combinedQualityVoices = combinedVoices.filter(
    (voice) => typeof voice.qualityScore === 'number' && voice.qualityScore > 0
  );

  // Load user data
  useEffect(() => {
    loadUserData();
  }, [user.id]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      // Load favorites
      const favRef = userSubcollectionRef(user.id, SUBCOLLECTIONS.FAVORITES);
      const favQuery = query(favRef, orderBy('addedAt', 'desc'), limit(50));
      const favSnapshot = await getDocs(favQuery);
      setFavorites(favSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as FavoriteWell)));

      // Load well history
      const histRef = userSubcollectionRef(user.id, SUBCOLLECTIONS.WELL_HISTORY);
      const histQuery = query(histRef, orderBy('visitedAt', 'desc'), limit(50));
      const histSnapshot = await getDocs(histQuery);
      setWellHistory(histSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as WellHistoryEntry)));

      // Load notifications
      const notifRef = userSubcollectionRef(user.id, SUBCOLLECTIONS.NOTIFICATIONS);
      const notifQuery = query(notifRef, orderBy('createdAt', 'desc'), limit(20));
      const notifSnapshot = await getDocs(notifQuery);
      setNotifications(notifSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AppNotification)));

      // Load audit logs for this user
      const logs = await getLogsForTarget(user.id, 50);
      setAuditLogs(logs as unknown as AdminAuditLog[]);

      // Load voice clones
      try {
        const [voices, userCreatedVoices, userMediaClonedVoices] = await Promise.all([
          getUserVoices(user.id),
          getUserCreatedVoices(user.id),
          getUserMediaClonedVoices(user.id)
        ]);
        setVoiceClones(voices);
        setCreatedVoices(userCreatedVoices);
        setMediaClonedVoices(userMediaClonedVoices);
      } catch (e) {
        console.warn('Failed to load voice data:', e);
      }

      // Load hazard reports
      try {
        const hazardRef = userSubcollectionRef(user.id, SUBCOLLECTIONS.HAZARD_REPORTS);
        const hazardQuery = query(hazardRef, orderBy('reportedAt', 'desc'), limit(50));
        const hazardSnapshot = await getDocs(hazardQuery);
        setHazardReports(hazardSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as UserHazardReport)));
      } catch (e) {
        console.warn('Failed to load hazard reports:', e);
      }

      // Load cancellation feedback
      try {
        const cancelRef = userSubcollectionRef(user.id, 'cancellation_feedback');
        const cancelQuery = query(cancelRef, orderBy('createdAt', 'desc'), limit(10));
        const cancelSnapshot = await getDocs(cancelQuery);
        setCancellationFeedback(cancelSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CancellationFeedbackEntry)));
      } catch (e) {
        console.warn('Failed to load cancellation feedback:', e);
      }

      // Load transactions (billing)
      try {
        const txns = await getUserTransactions(user.id, 100);
        setTransactions(txns);
        const revenue = txns
          .filter(t => t.status === 'completed' && t.type !== 'refund')
          .reduce((sum, t) => sum + (t.amount || 0), 0);
        setTotalRevenue(revenue);
      } catch (e) {
        console.warn('Failed to load transactions:', e);
      }

      // Load referral stats
      try {
        const stats = await getUserReferralStats(user.id);
        setReferralStats(stats);
      } catch (e) {
        console.warn('Failed to load referral stats:', e);
      }

      // Load referral invites
      try {
        const invites = await getUserReferralInvites(user.id);
        setReferralInvites(invites);
      } catch (e) {
        console.warn('Failed to load referral invites:', e);
      }

      // Load analytics events
      try {
        const events = await getUserEvents(user.id, 50);
        setUserEvents(events);
      } catch (e) {
        console.warn('Failed to load user events:', e);
      }

      // Load login attempts
      try {
        const attempts = await getLoginAttempts({ userId: user.id });
        setLoginAttempts(attempts);
      } catch (e) {
        console.warn('Failed to load login attempts:', e);
      }

      // Load support profile
      try {
        const profile = await lookupUserForSupport(user.id);
        if (profile) {
          setSupportProfile({ tickets: profile.tickets, feedback: profile.feedback });
        }
      } catch (e) {
        console.warn('Failed to load support profile:', e);
      }

    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load some user data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCreatedVoice = async (voice: CreatedVoiceProfile) => {
    const soldCount = voice.downloadCount || 0;
    const soldWarning = soldCount > 0
      ? ` This voice already has ${soldCount} marketplace download${soldCount === 1 ? '' : 's'}. Existing buyers will keep their private copies.`
      : '';
    if (!confirm(`Delete created voice "${voice.name}"? This cannot be undone.${soldWarning}`)) {
      return;
    }

    const processingKey = `created_${voice.id}`;
    setVoiceProcessing(prev => new Set([...prev, processingKey]));

    try {
      const result = await deleteCreatedVoice(user.id, voice.id, adminId, 'Admin deletion from user detail modal');
      if (result.success) {
        toast.success(`Created voice "${voice.name}" deleted`);
        await loadUserData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete created voice');
    } finally {
      setVoiceProcessing(prev => {
        const next = new Set(prev);
        next.delete(processingKey);
        return next;
      });
    }
  };

  const handleUnpublishCreatedVoice = async (voice: CreatedVoiceProfile) => {
    if (!confirm(`Remove "${voice.name}" from the marketplace? Existing buyers keep their copy, but new users will not be able to download it.`)) {
      return;
    }

    const processingKey = `created_unpublish_${voice.id}`;
    setVoiceProcessing(prev => new Set([...prev, processingKey]));

    try {
      const result = await unpublishCreatedVoice(user.id, voice.id, adminId, 'Admin unpublish from user detail modal');
      if (result.success) {
        toast.success(`Created voice "${voice.name}" removed from marketplace`);
        await loadUserData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to unpublish created voice');
    } finally {
      setVoiceProcessing(prev => {
        const next = new Set(prev);
        next.delete(processingKey);
        return next;
      });
    }
  };

  const handleDeleteMediaClonedVoice = async (voice: MediaClonedVoiceProfile) => {
    const processingKey = `media_${voice.id}`;
    setVoiceProcessing(prev => new Set([...prev, processingKey]));

    try {
      const result = await deleteMediaClonedVoice(user.id, voice.id, adminId, 'Admin deletion from user detail modal');
      if (result.success) {
        toast.success(`Media clone "${voice.name}" deleted`);
        await loadUserData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete media clone');
    } finally {
      setVoiceProcessing(prev => {
        const next = new Set(prev);
        next.delete(processingKey);
        return next;
      });
    }
  };

  const formatDate = (date: unknown): string => {
    if (!date) return 'Never';
    const d = timestampToDate(date);
    return d?.toLocaleDateString() || 'Invalid date';
  };

  const formatDateTime = (date: unknown): string => {
    if (!date) return 'Never';
    const d = timestampToDate(date);
    return d?.toLocaleString() || 'Invalid date';
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'SUBSCRIBED': 'bg-green-100 text-green-800',
      'active': 'bg-green-100 text-green-800',
      'TRIAL_ACTIVE': 'bg-blue-100 text-blue-800',
      'trial': 'bg-blue-100 text-blue-800',
      'TRIAL_EXPIRED': 'bg-red-100 text-red-800',
      'SUBSCRIPTION_EXPIRED': 'bg-orange-100 text-orange-800',
      'expired': 'bg-red-100 text-red-800',
      'BLOCKED': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800',
      'REFERRAL_FREE_MONTH': 'bg-pink-100 text-pink-800',
      'invited': 'bg-purple-100 text-purple-800',
      'NONE': 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getDaysRemaining = (endDate: unknown): number | null => {
    if (!endDate) return null;
    const end = timestampToDate(endDate);
    if (!end) return null;
    return Math.ceil((end.getTime() - Date.now()) / MILLISECONDS_PER_DAY);
  };

  const getDaysSinceSignup = (): number => {
    const created = timestampToDate(user.createdAt);
    if (!created) return 0;
    return Math.floor((Date.now() - created.getTime()) / MILLISECONDS_PER_DAY);
  };

  const getReferralInviteStatusColor = (status?: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-700';
      case 'SUBSCRIBED_MONTH_1': return 'bg-emerald-100 text-emerald-700';
      case 'TRIAL_ACTIVE': return 'bg-blue-100 text-blue-700';
      case 'SIGNED_UP': return 'bg-cyan-100 text-cyan-700';
      case 'INVITED': return 'bg-gray-100 text-gray-700';
      case 'CHURNED': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'purchase': return 'bg-green-100 text-green-700';
      case 'renewal': return 'bg-blue-100 text-blue-700';
      case 'refund': return 'bg-red-100 text-red-700';
      case 'cancellation': return 'bg-orange-100 text-orange-700';
      case 'trial_start': return 'bg-purple-100 text-purple-700';
      case 'trial_convert': return 'bg-emerald-100 text-emerald-700';
      case 'admin_grant': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'refunded': return 'bg-red-100 text-red-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
              {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
            </div>
            <div>
              <h2 className="text-xl font-semibold">{user.displayName || 'No Name'}</h2>
              <p className="text-amber-100">{user.email || user.phone || 'No contact info'}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getStatusBadge(user.subscriptionStatus)}>
                  {user.subscriptionStatus}
                </Badge>
                {user.referralCode && (
                  <Badge variant="outline" className="text-white border-white/50">
                    Code: {user.referralCode}
                  </Badge>
                )}
                {totalRevenue > 0 && (
                  <Badge variant="outline" className="text-white border-white/50">
                    <DollarSign className="h-3 w-3 mr-0.5" />
                    {totalRevenue.toFixed(2)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={loadUserData}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onEdit}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <button onClick={onClose} className="hover:bg-white/20 p-1 rounded">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full justify-start border-b rounded-none px-4 flex-shrink-0 flex-wrap">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
            <TabsTrigger value="referrals">Referrals ({referralInvites.length})</TabsTrigger>
            <TabsTrigger value="voices">Voices ({voiceClones.length})</TabsTrigger>
            <TabsTrigger value="hazards">Hazards ({hazardReports.length})</TabsTrigger>
            <TabsTrigger value="favorites">Favorites ({favorites.length})</TabsTrigger>
            <TabsTrigger value="history">History ({wellHistory.length})</TabsTrigger>
            <TabsTrigger value="support">Support</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto p-4">
            {/* ==================== OVERVIEW TAB ==================== */}
            <TabsContent value="overview" className="m-0 space-y-4">
              {/* Revenue & Subscription Summary */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      Revenue & Subscription
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Spent</span>
                      <span className="font-bold text-green-600">${totalRevenue.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Monthly Value</span>
                      <span className="font-medium">${SUBSCRIPTION_PRICE}/mo</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Transactions</span>
                      <span className="font-medium">{transactions.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Status</span>
                      <Badge className={getStatusBadge(user.subscriptionStatus)}>
                        {user.subscriptionStatus}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="flex-1">{user.email || 'No email'}</span>
                      {user.emailVerified && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="flex-1">{user.phone || 'No phone'}</span>
                      {user.phoneVerified && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span>{user.company || 'No company'}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Activity Summary Row */}
              <div className="grid grid-cols-5 gap-3">
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="pt-4 text-center">
                    <Clock className="h-5 w-5 text-indigo-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-indigo-600">{getDaysSinceSignup()}</p>
                    <p className="text-xs text-gray-500">Days Active</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4 text-center">
                    <Activity className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-blue-600">{user.loginCount || 0}</p>
                    <p className="text-xs text-gray-500">Logins</p>
                  </CardContent>
                </Card>
                <Card className="bg-cyan-50 border-cyan-200">
                  <CardContent className="pt-4 text-center">
                    <Mic className="h-5 w-5 text-cyan-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-cyan-600">
                      {voiceClones.length}
                      {voiceClones.filter(v => v.isPaid).length > 0 && (
                        <span className="text-xs text-gray-400 ml-1">
                          ({voiceClones.filter(v => v.isPaid).length} paid)
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">Voices</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-4 text-center">
                    <Users className="h-5 w-5 text-purple-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-purple-600">
                      {referralStats?.totalSignups ?? 0}/{referralStats?.totalInvitesSent ?? user.totalInvitesSent ?? user.referralCount ?? 0}
                    </p>
                    <p className="text-xs text-gray-500">Referrals</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="pt-4 text-center">
                    <AlertTriangle className="h-5 w-5 text-orange-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-orange-600">{Math.max(user.hazardsReported || 0, user.hazardPoints || 0)}</p>
                    <p className="text-xs text-gray-500">Hazards</p>
                  </CardContent>
                </Card>
              </div>

              {/* Device Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    Device Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Platform</p>
                    <p className="font-medium capitalize">{user.platform || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">App Version</p>
                    <p className="font-medium">{user.appVersion || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Device</p>
                    <p className="font-medium">{user.deviceModel || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Login</p>
                    <p className="font-medium">{formatDate(user.lastLoginAt)}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Created</p>
                    <p className="font-medium">{formatDateTime(user.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Login</p>
                    <p className="font-medium">{formatDateTime(user.lastLoginAt)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Updated</p>
                    <p className="font-medium">{formatDateTime(user.updatedAt)}</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ==================== SUBSCRIPTION TAB ==================== */}
            <TabsContent value="subscription" className="m-0 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Subscription Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Current Status</p>
                      <Badge className={`${getStatusBadge(user.subscriptionStatus)} text-lg px-3 py-1 mt-1`}>
                        {user.subscriptionStatus}
                      </Badge>
                    </div>
                    {(user.trialEndDate || user.subscriptionEndDate) && (
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {user.subscriptionStatus?.includes('TRIAL') ? 'Trial Ends' : 'Subscription Ends'}
                        </p>
                        <p className="text-lg font-bold">
                          {formatDate(user.trialEndDate || user.subscriptionEndDate)}
                        </p>
                        {getDaysRemaining(user.trialEndDate || user.subscriptionEndDate) !== null && (
                          <p className={`text-sm ${getDaysRemaining(user.trialEndDate || user.subscriptionEndDate)! <= 3 ? 'text-red-600' : 'text-gray-500'}`}>
                            {getDaysRemaining(user.trialEndDate || user.subscriptionEndDate)} days remaining
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Trial Start</p>
                      <p className="font-medium">{formatDate(user.trialStartDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Trial End</p>
                      <p className="font-medium">{formatDate(user.trialEndDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Subscription Start</p>
                      <p className="font-medium">{formatDate(user.subscriptionStartDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Subscription End</p>
                      <p className="font-medium">{formatDate(user.subscriptionEndDate)}</p>
                    </div>
                  </div>

                  {user.revenueCatCustomerId && (
                    <div className="pt-4 border-t flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">RevenueCat Customer ID</p>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{user.revenueCatCustomerId}</code>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`https://app.revenuecat.com/customers/${user.revenueCatCustomerId}`, '_blank')}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open Dashboard
                      </Button>
                    </div>
                  )}

                  {/* Referral Info */}
                  {(user.referredBy || user.freeMonthFromReferral) && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium text-gray-700 mb-2">Referral Information</p>
                      {user.referredBy && (
                        <p className="text-sm text-gray-600">Referred by: <code className="bg-gray-100 px-1 rounded">{user.referredBy}</code></p>
                      )}
                      {user.freeMonthFromReferral && (
                        <Badge className="bg-pink-100 text-pink-800 mt-2">Free Month from Referral</Badge>
                      )}
                    </div>
                  )}

                  {/* Free Months Available */}
                  {(user.freeMonthsAvailable || 0) > 0 && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-800">
                        <Gift className="h-4 w-4 inline mr-1" />
                        {user.freeMonthsAvailable} free month(s) available
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment History */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Payment History ({transactions.length})
                    </div>
                    {totalRevenue > 0 && (
                      <span className="text-green-600 font-bold">
                        Total: ${totalRevenue.toFixed(2)}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {transactions.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No transactions recorded</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {transactions.map(txn => (
                        <div key={txn.id} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge className={`text-xs ${getTransactionTypeColor(txn.type)}`}>
                                {txn.type.replace(/_/g, ' ')}
                              </Badge>
                              {txn.store && (
                                <span className="text-xs text-gray-400">{txn.store}</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDateTime(txn.timestamp)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${txn.type === 'refund' ? 'text-red-600' : 'text-green-600'}`}>
                              {txn.type === 'refund' ? '-' : ''}${(txn.amount || 0).toFixed(2)}
                            </p>
                            <Badge className={`text-xs ${getTransactionStatusColor(txn.status)}`}>
                              {txn.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ==================== REFERRALS TAB (NEW) ==================== */}
            <TabsContent value="referrals" className="m-0 space-y-4">
              {/* Referral Stats */}
              <div className="grid grid-cols-4 gap-3">
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-4 text-center">
                    <Send className="h-5 w-5 text-purple-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-purple-600">
                      {referralStats?.totalInvitesSent || 0}
                    </p>
                    <p className="text-xs text-gray-500">Invites Sent</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4 text-center">
                    <Users className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-blue-600">
                      {referralStats?.totalSignups || 0}
                    </p>
                    <p className="text-xs text-gray-500">Signups</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4 text-center">
                    <CreditCard className="h-5 w-5 text-green-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-green-600">
                      {referralStats?.totalPaidMonth1 || 0}
                    </p>
                    <p className="text-xs text-gray-500">Paid Month 1</p>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="pt-4 text-center">
                    <Gift className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-yellow-600">
                      {referralStats?.freeMonthsAvailable || 0}
                    </p>
                    <p className="text-xs text-gray-500">Free Months</p>
                  </CardContent>
                </Card>
              </div>

              {/* Referral Code Display */}
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Referral Code</p>
                      <p className="text-3xl font-bold tracking-wider text-purple-700">
                        {user.referralCode || 'N/A'}
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-gray-500">Credits Earned: <span className="font-bold text-green-600">{referralStats?.totalCreditsEarned || 0}</span></p>
                      <p className="text-gray-500">Pending: <span className="font-bold text-yellow-600">{referralStats?.pendingCredits || 0}</span></p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Referred By */}
              {user.referredBy && (
                <Card className="border-pink-200 bg-pink-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-pink-700 flex items-center gap-2">
                      <ChevronRight className="h-4 w-4" />
                      Referred By
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <code className="text-sm bg-pink-100 px-2 py-1 rounded">{user.referredBy}</code>
                        {user.referredByCode && (
                          <span className="text-xs text-gray-500 ml-2">Code: {user.referredByCode}</span>
                        )}
                      </div>
                      {user.freeMonthFromReferral && (
                        <Badge className="bg-pink-200 text-pink-800">
                          <Gift className="h-3 w-3 mr-1" />
                          Received Free Month
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Referral Invites List */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Sent Invitations ({referralInvites.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {referralInvites.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No invites sent yet</p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {referralInvites.map(invite => (
                        <div key={invite.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{invite.recipientName || invite.recipientContact}</span>
                                <Badge variant="outline" className="text-xs">
                                  {invite.inviteMethod}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Sent {formatDate(invite.inviteSentAt)}
                              </p>
                            </div>
                            <div className="flex flex-col gap-1 items-end">
                              <Badge className={`text-xs ${getReferralInviteStatusColor(invite.status)}`}>
                                {(invite.status || 'INVITED').replace(/_/g, ' ')}
                              </Badge>
                            </div>
                          </div>
                          {/* Pipeline detail */}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {invite.hasSignedUp && (
                              <Badge className="text-xs bg-cyan-100 text-cyan-700">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Signed Up {invite.signupAt ? formatDate(invite.signupAt) : ''}
                              </Badge>
                            )}
                            {invite.month1PaidAt && (
                              <Badge className="text-xs bg-green-100 text-green-700">
                                <DollarSign className="h-3 w-3 mr-0.5" />
                                Month 1 Paid
                              </Badge>
                            )}
                            {invite.month2PaidAt && (
                              <Badge className="text-xs bg-emerald-100 text-emerald-700">
                                <DollarSign className="h-3 w-3 mr-0.5" />
                                Month 2 Paid
                              </Badge>
                            )}
                            {invite.referrerCredited && (
                              <Badge className="text-xs bg-yellow-100 text-yellow-700">
                                <Gift className="h-3 w-3 mr-1" />
                                Credit Applied
                              </Badge>
                            )}
                            {!invite.hasSignedUp && (
                              <span className="text-xs text-gray-400">Awaiting signup</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ==================== VOICES TAB ==================== */}
            <TabsContent value="voices" className="m-0 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                </div>
              ) : (
                <>
                  {/* Voice Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4 text-center">
                        <Volume2 className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                        <p className="text-xl font-bold text-blue-600">{combinedVoices.length}</p>
                        <p className="text-xs text-gray-500">Total Voices</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-4 text-center">
                        <Crown className="h-5 w-5 text-green-500 mx-auto mb-1" />
                        <p className="text-xl font-bold text-green-600">
                          {combinedVoices.filter(v => v.isPaid).length}
                        </p>
                        <p className="text-xs text-gray-500">Paid Voices</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="pt-4 text-center">
                        <Star className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                        <p className="text-xl font-bold text-amber-600">
                          {combinedQualityVoices.length > 0
                            ? ((combinedQualityVoices.reduce((sum, v) => sum + (v.qualityScore || 0), 0) / combinedQualityVoices.length) * 100).toFixed(0)
                            : 0}%
                        </p>
                        <p className="text-xs text-gray-500">Avg Quality</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Voice List */}
                  {voiceClones.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Mic className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No voice clones configured</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {voiceClones.map(voice => {
                        const processingKey = `clone_${voice.id}`;
                        const isProcessing = voiceProcessing.has(processingKey);

                        const handleToggleVoice = async () => {
                          setVoiceProcessing(prev => new Set([...prev, processingKey]));
                          try {
                            await toggleVoiceActive(user.id, voice.id, !voice.isActive, adminId);
                            toast.success(voice.isActive ? 'Voice deactivated' : 'Voice activated');
                            loadUserData();
                          } catch (error) {
                            toast.error(error instanceof Error ? error.message : 'Failed to toggle voice');
                          } finally {
                            setVoiceProcessing(prev => {
                              const next = new Set(prev);
                              next.delete(processingKey);
                              return next;
                            });
                          }
                        };

                        const handleRetryVoice = async () => {
                          setVoiceProcessing(prev => new Set([...prev, processingKey]));
                          try {
                            const result = await retryVoiceProcessing(user.id, voice.id, adminId);
                            if (result.success) {
                              toast.success('Processing started');
                              loadUserData();
                            } else {
                              toast.error(result.message);
                            }
                          } catch (error) {
                            toast.error(error instanceof Error ? error.message : 'Retry failed');
                          } finally {
                            setVoiceProcessing(prev => {
                              const next = new Set(prev);
                              next.delete(processingKey);
                              return next;
                            });
                          }
                        };

                        return (
                          <Card
                            key={voice.id}
                            className={`hover:bg-gray-50 ${
                              voice.recordingStatus === 'FAILED' ? 'border-red-200 bg-red-50' :
                              voice.recordingStatus === 'READY' ? 'border-green-200' : ''
                            }`}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-full ${voice.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                                    <Volume2 className={`h-5 w-5 ${voice.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{voice.name}</span>
                                      {voice.nickname && (
                                        <span className="text-sm text-gray-500">"{voice.nickname}"</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge variant="outline" className="text-xs font-semibold capitalize">{voice.relationship}</Badge>
                                      <Badge className={`text-xs ${getVoiceStatusColor(voice.recordingStatus)}`}>
                                        {voice.recordingStatus}
                                      </Badge>
                                      {voice.isPaid ? (
                                        <Badge className="text-xs bg-green-100 text-green-700">Paid</Badge>
                                      ) : (
                                        <Badge className="text-xs bg-gray-100 text-gray-600">Unpaid</Badge>
                                      )}
                                      {voice.qualityScore !== undefined && (
                                        <span className={`text-xs font-medium ${
                                          voice.qualityScore >= 0.8 ? 'text-green-600' :
                                          voice.qualityScore >= 0.6 ? 'text-yellow-600' : 'text-red-600'
                                        }`}>
                                          {(voice.qualityScore * 100).toFixed(0)}% quality
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleToggleVoice}
                                    disabled={isProcessing}
                                    title={voice.isActive ? 'Deactivate' : 'Activate'}
                                  >
                                    {isProcessing ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Power className={`h-4 w-4 ${voice.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                                    )}
                                  </Button>
                                  {voice.recordingStatus === 'FAILED' && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleRetryVoice}
                                      disabled={isProcessing}
                                      title="Retry Processing"
                                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                    >
                                      {isProcessing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RotateCcw className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                              {voice.lastError && (
                                <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                                  Error: {voice.lastError}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Media Clones</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {mediaClonedVoices.length === 0 ? (
                        <div className="text-center py-6 text-gray-500">
                          <Zap className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p>No media-cloned voices found</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {mediaClonedVoices.map((voice) => {
                            const processingKey = `media_${voice.id}`;
                            const isProcessing = voiceProcessing.has(processingKey);

                            return (
                              <Card key={voice.id} className="hover:bg-gray-50">
                                <CardContent className="p-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                      <div className={`p-2 rounded-full ${voice.isPaid ? 'bg-sky-100' : 'bg-gray-100'}`}>
                                        <Zap className={`h-5 w-5 ${voice.isPaid ? 'text-sky-600' : 'text-gray-400'}`} />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{voice.name}</span>
                                          <Badge className={`text-xs ${getVoiceStatusColor(voice.status)}`}>
                                            {voice.status}
                                          </Badge>
                                          {voice.isPaid ? (
                                            <Badge className="text-xs bg-green-100 text-green-700">Paid</Badge>
                                          ) : (
                                            <Badge className="text-xs bg-gray-100 text-gray-600">Unpaid</Badge>
                                          )}
                                          <Badge className="text-xs bg-sky-100 text-sky-700">
                                            {voice.sourceType || 'Unknown Source'}
                                          </Badge>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                          <span>{voice.sourceUrl || voice.uploadedFileName || 'Private source'}</span>
                                          {voice.qualityScore !== undefined && (
                                            <>
                                              <span>•</span>
                                              <span>{(voice.qualityScore * 100).toFixed(0)}% quality</span>
                                            </>
                                          )}
                                        </div>
                                        {voice.failureMessage && (
                                          <p className="mt-1 text-xs text-red-600 line-clamp-2 max-w-xl">{voice.failureMessage}</p>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteMediaClonedVoice(voice)}
                                      disabled={isProcessing}
                                      className="text-red-600 border-red-300 hover:bg-red-50"
                                      title="Delete Media Clone"
                                    >
                                      {isProcessing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Created Voices</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {createdVoices.length === 0 ? (
                        <div className="text-center py-6 text-gray-500">
                          <Volume2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p>No created voices found</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {createdVoices.map((voice) => {
                            const processingKey = `created_${voice.id}`;
                            const isProcessing = voiceProcessing.has(processingKey) || voiceProcessing.has(`created_unpublish_${voice.id}`);

                            return (
                              <Card key={voice.id} className="hover:bg-gray-50">
                                <CardContent className="p-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                      <div className={`p-2 rounded-full ${voice.isPaid ? 'bg-violet-100' : 'bg-gray-100'}`}>
                                        <Volume2 className={`h-5 w-5 ${voice.isPaid ? 'text-violet-600' : 'text-gray-400'}`} />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{voice.name}</span>
                                          <Badge className={`text-xs ${getVoiceStatusColor(voice.status)}`}>
                                            {voice.status}
                                          </Badge>
                                          {voice.isPaid ? (
                                            <Badge className="text-xs bg-green-100 text-green-700">Paid</Badge>
                                          ) : (
                                            <Badge className="text-xs bg-gray-100 text-gray-600">Unpaid</Badge>
                                          )}
                                          {voice.isPublic ? (
                                            <Badge className="text-xs bg-blue-100 text-blue-700">Public</Badge>
                                          ) : (
                                            <Badge className="text-xs bg-slate-100 text-slate-600">Private</Badge>
                                          )}
                                          {(voice.downloadCount || 0) > 0 ? (
                                            <Badge className="text-xs bg-amber-100 text-amber-700">
                                              {voice.downloadCount} downloads
                                            </Badge>
                                          ) : null}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                          <span>{voice.gender || 'Unknown gender'}</span>
                                          <span>•</span>
                                          <span>{voice.age || 'Unknown age'}</span>
                                          {voice.qualityScore !== undefined && (
                                            <>
                                              <span>•</span>
                                              <span>{(voice.qualityScore * 100).toFixed(0)}% quality</span>
                                            </>
                                          )}
                                        </div>
                                        {voice.description && (
                                          <p className="mt-1 text-xs text-gray-600 line-clamp-2 max-w-xl">{voice.description}</p>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteCreatedVoice(voice)}
                                      disabled={isProcessing}
                                      className="text-red-600 border-red-300 hover:bg-red-50"
                                      title="Delete Created Voice"
                                    >
                                      {isProcessing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="h-4 w-4" />
                                      )}
                                    </Button>
                                    {voice.isPublic ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleUnpublishCreatedVoice(voice)}
                                        disabled={isProcessing}
                                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                        title="Remove From Marketplace"
                                      >
                                        {isProcessing ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <XCircle className="h-4 w-4" />
                                        )}
                                      </Button>
                                    ) : null}
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* ==================== HAZARDS TAB ==================== */}
            <TabsContent value="hazards" className="m-0 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                </div>
              ) : hazardReports.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No hazard reports</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Hazard Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-orange-50">
                      <CardContent className="pt-4 text-center">
                        <AlertTriangle className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-orange-600">{hazardReports.length}</p>
                        <p className="text-xs text-gray-500">Total Reports</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="pt-4 text-center">
                        <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-green-600">
                          {hazardReports.filter(h => h.verified).length}
                        </p>
                        <p className="text-xs text-gray-500">Verified</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50">
                      <CardContent className="pt-4 text-center">
                        <Star className="h-6 w-6 text-purple-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-purple-600">
                          {hazardReports.reduce((sum, h) => sum + (h.pointsEarned || 0), 0)}
                        </p>
                        <p className="text-xs text-gray-500">Points Earned</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Hazard List */}
                  <div className="space-y-2">
                    {hazardReports.map((report, index) => (
                        <Card key={report.hazardId || index} className="hover:bg-gray-50">
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <AlertTriangle className={`h-5 w-5 ${
                                  report.verified ? 'text-green-500' : 'text-orange-500'
                                }`} />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={getHazardTypeColor(report.type)}>
                                      {report.type?.replace(/_/g, ' ') || 'Unknown'}
                                    </Badge>
                                    {report.verified && (
                                      <Badge className="bg-green-100 text-green-700 text-xs">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Verified
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Reported {formatDateTime(report.reportedAt)}
                                    {report.pointsEarned ? ` • +${report.pointsEarned} pts` : ''}
                                  </p>
                                  {report.latitude && report.longitude && (
                                    <p className="text-xs text-gray-400">
                                      <MapPin className="h-3 w-3 inline mr-1" />
                                      {report.latitude.toFixed(4)}, {report.longitude.toFixed(4)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancellation Feedback Section */}
              {cancellationFeedback.length > 0 && (
                <Card className="mt-4 border-red-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-red-600">
                      <XCircle className="h-4 w-4" />
                      Cancellation Feedback ({cancellationFeedback.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {cancellationFeedback.map((feedback, index) => (
                        <div key={feedback.id || index} className="p-3 bg-red-50 rounded-lg">
                          <p className="text-sm font-medium text-red-800">
                            {feedback.reason || 'No reason provided'}
                          </p>
                          {feedback.feedback && (
                            <p className="text-xs text-red-600 mt-1">"{feedback.feedback}"</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDateTime(feedback.createdAt)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ==================== FAVORITES TAB ==================== */}
            <TabsContent value="favorites" className="m-0 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                </div>
              ) : favorites.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Star className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No favorites saved</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {favorites.map(fav => (
                    <Card key={fav.wellId} className="hover:bg-gray-50">
                      <CardContent className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Star className="h-5 w-5 text-yellow-500" />
                          <div>
                            <p className="font-medium">{fav.wellName}</p>
                            <p className="text-xs text-gray-500">
                              {fav.operatorName && `${fav.operatorName} • `}
                              Added {formatDate(fav.addedAt)}
                            </p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          <MapPin className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ==================== HISTORY TAB ==================== */}
            <TabsContent value="history" className="m-0 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                </div>
              ) : wellHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Navigation className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No navigation history</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {wellHistory.map((entry, index) => (
                    <Card key={index} className="hover:bg-gray-50">
                      <CardContent className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Navigation className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium">{entry.wellName}</p>
                            <p className="text-xs text-gray-500">
                              Visited {formatDateTime(entry.visitedAt)}
                              {entry.distance && ` • ${entry.distance.toFixed(1)} mi`}
                              {entry.navigationTime && ` • ${entry.navigationTime} min`}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ==================== SUPPORT TAB (NEW) ==================== */}
            <TabsContent value="support" className="m-0 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                </div>
              ) : !supportProfile ? (
                <div className="text-center py-8 text-gray-500">
                  <HelpCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No support data available</p>
                </div>
              ) : (
                <>
                  {/* Support Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="pt-4 text-center">
                        <AlertCircle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                        <p className="text-xl font-bold text-red-600">
                          {supportProfile.tickets.filter(t => t.status === 'open').length}
                        </p>
                        <p className="text-xs text-gray-500">Open Tickets</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-yellow-50 border-yellow-200">
                      <CardContent className="pt-4 text-center">
                        <MessageSquare className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
                        <p className="text-xl font-bold text-yellow-600">
                          {supportProfile.feedback.length}
                        </p>
                        <p className="text-xs text-gray-500">Feedback Items</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4 text-center">
                        <HelpCircle className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                        <p className="text-xl font-bold text-blue-600">
                          {supportProfile.tickets.length}
                        </p>
                        <p className="text-xs text-gray-500">Total Tickets</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Support Tickets */}
                  {supportProfile.tickets.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <HelpCircle className="h-4 w-4" />
                          Support Tickets ({supportProfile.tickets.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {supportProfile.tickets.map(ticket => (
                              <div key={ticket.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{ticket.subject}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {ticket.category} • {formatDate(ticket.createdAt)}
                                    </p>
                                  </div>
                                  <div className="flex flex-col gap-1 items-end">
                                    <Badge className={`text-xs ${getTicketStatusColor(ticket.status)}`}>
                                      {ticket.status.replace(/_/g, ' ')}
                                    </Badge>
                                    <Badge className={`text-xs ${getTicketPriorityColor(ticket.priority)}`}>
                                      {ticket.priority}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* User Feedback */}
                  {supportProfile.feedback.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          User Feedback ({supportProfile.feedback.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {supportProfile.feedback.map(fb => (
                            <div key={fb.id} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs capitalize">{fb.type}</Badge>
                                    <Badge variant="outline" className="text-xs">{fb.source}</Badge>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-1">{fb.content}</p>
                                  <p className="text-xs text-gray-400 mt-1">{formatDate(fb.createdAt)}</p>
                                </div>
                                {fb.rating && (
                                  <div className="flex gap-0.5 ml-2">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <Star
                                        key={i}
                                        className={`h-3 w-3 ${
                                          i < fb.rating! ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Empty state for both */}
                  {supportProfile.tickets.length === 0 && supportProfile.feedback.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <HelpCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No support tickets or feedback from this user</p>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* ==================== ACTIVITY TAB ==================== */}
            <TabsContent value="activity" className="m-0 space-y-4">
              {/* App Events */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MousePointerClick className="h-4 w-4" />
                    App Events ({userEvents.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {userEvents.length === 0 ? (
                    <p className="text-gray-500 text-sm">No events recorded</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {userEvents.slice(0, 25).map((event, idx) => (
                        <div key={event.id || idx} className="text-sm p-2 bg-gray-50 rounded flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium capitalize">{event.eventType.replace(/_/g, ' ')}</p>
                            {event.eventData && Object.keys(event.eventData).length > 0 && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                                {Object.entries(event.eventData).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(', ')}
                              </p>
                            )}
                            {event.deviceInfo && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {event.deviceInfo.platform} {event.deviceInfo.version}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{formatDateTime(event.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Login History */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Login History ({loginAttempts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loginAttempts.length === 0 ? (
                    <p className="text-gray-500 text-sm">No login attempts recorded</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {loginAttempts.map((attempt, idx) => (
                        <div key={attempt.id || idx} className="text-sm p-2 bg-gray-50 rounded flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              {attempt.success ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                              <p className="font-medium capitalize">{attempt.method}</p>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              IP: {attempt.ipAddress || 'Unknown'}
                              {attempt.location && ` • ${attempt.location}`}
                            </p>
                            {!attempt.success && attempt.failureReason && (
                              <p className="text-xs text-red-600 mt-1">{attempt.failureReason}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{formatDateTime(attempt.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notifications */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Recent Notifications ({notifications.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <p className="text-gray-500 text-sm">No notifications</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {notifications.slice(0, 10).map(notif => (
                        <div key={notif.id} className="text-sm p-2 bg-gray-50 rounded flex justify-between">
                          <div>
                            <p className="font-medium">{notif.title}</p>
                            <p className="text-xs text-gray-500">{notif.body}</p>
                          </div>
                          <span className="text-xs text-gray-400">{formatDate(notif.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Admin Activity */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Admin Activity Log ({auditLogs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {auditLogs.length === 0 ? (
                    <p className="text-gray-500 text-sm">No admin activity recorded</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {auditLogs.map(log => (
                        <div key={log.id} className="text-sm p-2 bg-gray-50 rounded flex justify-between items-start">
                          <div>
                            <p className="font-medium">{log.action.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-gray-500">
                              by {log.adminId.slice(0, 8)}...
                            </p>
                          </div>
                          <span className="text-xs text-gray-400">{formatDateTime(log.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center bg-gray-50 flex-shrink-0">
          <div className="text-xs text-gray-400">
            User ID: <code className="bg-gray-200 px-1 rounded">{user.id}</code>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={onDelete}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
            <Button
              onClick={onEdit}
              variant="amber"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit User
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
